// API base URL
const API_BASE_URL = 'https://api.lab.battlexo.com/api/v1';

// Default request options
const defaultOptions = {
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  }
};

/**
 * Fetches all games from the server
 * @returns {Promise<Array>} A promise that resolves to an array of games
 */
export const fetchGames = async () => {
  try {
    console.log('Fetching games from API...');
    const response = await fetch(`${API_BASE_URL}/html/list`, defaultOptions);
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log(`Successfully fetched ${data.length} games`);
    return data;
  } catch (error) {
    console.error('Error fetching games:', error); 
    throw error;
  }
};

/**
 * Fetches a specific game by ID
 * @param {string} fileId - The ID of the game to fetch
 * @returns {Promise<Object>} A promise that resolves to the game data
 */
export const fetchGameById = async (fileId) => {
  try {
    console.log(`Fetching game with ID: ${fileId}`);
    const response = await fetch(`${API_BASE_URL}/html/get/${fileId}`, defaultOptions);
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('Successfully fetched game data');
    return data;
  } catch (error) {
    console.error(`Error fetching game with ID ${fileId}:`, error);
    throw error;
  }
};

/**
 * Publishes a game and generates a shareable link
 * @param {string} sessionId - The ID of the chat session
 * @param {string} userId - The ID of the user
 * @param {string} htmlContent - The HTML content of the game
 * @param {string} title - The title of the game
 * @param {Object} metadata - Additional metadata for the game (e.g. remix information)
 * @returns {Promise<Object>} A promise that resolves to the published game data with a unique ID for sharing
 */
export const publishGame = async (sessionId, userId, htmlContent, title, metadata = {}) => {
  try {
    console.log(`Publishing game from session: ${sessionId}`);
    
    // Use the html/store endpoint instead of html/publish since publish doesn't exist
    const response = await fetch(`${API_BASE_URL}/html/store`, {
      ...defaultOptions,
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        session_id: sessionId,
        content: htmlContent,
        title: title || 'Untitled Game',
        is_remix: metadata.isRemix || false,
        original_id: metadata.originalId || null,
        metadata: metadata
      })
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('Successfully published game:', data);
    
    // Generate a shareable link
    const gameId = data.id || data.file_id;
    const shareableLink = `${window.location.origin}/play/${gameId}`;
    
    return {
      ...data,
      shareableLink
    };
  } catch (error) {
    console.error('Error publishing game:', error);
    throw error;
  }
};

/**
 * Checks if the API server is available
 * @returns {Promise<boolean>} A promise that resolves to true if the server is available
 */
export const checkServerAvailability = async () => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
    
    const response = await fetch(`${API_BASE_URL}/html/list`, {
      ...defaultOptions,
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    return response.ok;
  } catch (error) {
    console.error('Server availability check failed:', error);
    return false;
  }
};

export default {
  fetchGames,
  fetchGameById,
  publishGame,
  checkServerAvailability
}; 